import { useEffect, useRef, useCallback } from "react";
import { erpApi } from "../axios";
import { handleAxiosError, urlBackend } from "..";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth";

const DEFAULT_LOCK_RENEW_INTERVAL = 4 * 60 * 1000; // 4 minutos

/**
 * Hook para manejar bloqueo de registros a partir de la ruta en la que se encuentra el usuario, bloqueando el acceso desde el frontend.
 * @param recordId ID del registro a bloquear
 * @param LOCK_RENEW_INTERVAL Intervalo de renovación del bloqueo en milisegundos (por defecto 4 minutos)
 */
export function useRecordLock(
  recordId: string,
  LOCK_RENEW_INTERVAL: number = DEFAULT_LOCK_RENEW_INTERVAL
) {
  const { post, remove } = erpApi();
  const storedToken = useAuthStore.getState().token;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();
  const hasNavigated = useRef(false);
  const hasReleased = useRef(false);

  const lockUrl = `${urlBackend}api/v1/${recordId}/lock/`;
  const renewUrl = `${urlBackend}api/v1/${recordId}/lock/renew/`;

  const goBack = useCallback(
    (error?: any) => {
      if (hasNavigated.current) return;
      hasNavigated.current = true;
      if (error) handleAxiosError(error);
      navigate(-1);
    },
    [navigate]
  );

  // Liberación normal (desmontaje del componente / cambio de ruta dentro del SPA)
  const releaseLock = useCallback(() => {
    if (hasReleased.current || !recordId) return;
    hasReleased.current = true;
    remove(lockUrl, true).catch(() => {});
  }, [recordId, lockUrl, remove]);

  // Liberación al cerrar pestaña / recargar: axios normal puede no llegar a
  // completarse porque el navegador mata la petición al descargar la página.
  // fetch con keepalive sí sobrevive ese unload.
  const releaseLockOnUnload = useCallback(() => {
    if (hasReleased.current || !recordId) return;
    hasReleased.current = true;

    fetch(lockUrl, {
      method: "DELETE",
      keepalive: true,
      credentials: "include",
      headers: storedToken
        ? { Authorization: `Token ${storedToken}` }
        : undefined,
    }).catch(() => {});
  }, [recordId, lockUrl]);

  useEffect(() => {
    if (!recordId) return;

    let cancelled = false;
    hasReleased.current = false;
    hasNavigated.current = false;

    const acquireLock = async () => {
      try {
        await post<any>(lockUrl, {}, true);
        if (cancelled) return;

        intervalRef.current = setInterval(async () => {
          try {
            await post(renewUrl, {}, true);
          } catch (error) {
            console.error("Error renovando lock", error);
            if (intervalRef.current) clearInterval(intervalRef.current);
            goBack(error);
          }
        }, LOCK_RENEW_INTERVAL);
      } catch (error) {
        console.error("No fue posible adquirir el lock", error);
        goBack(error);
      }
    };

    acquireLock();

    // Tab cerrada, recargada, o navegación fuera del SPA (URL distinta escrita a mano, etc.)
    window.addEventListener("beforeunload", releaseLockOnUnload);
    window.addEventListener("pagehide", releaseLockOnUnload);

    return () => {
      cancelled = true;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      window.removeEventListener("beforeunload", releaseLockOnUnload);
      window.removeEventListener("pagehide", releaseLockOnUnload);

      // Cambio de ruta dentro del SPA (React Router) -> el componente se
      // desmonta y este cleanup se ejecuta normalmente.
      releaseLock();
    };
  }, [recordId, LOCK_RENEW_INTERVAL]);
}